import { Button, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography } from "@mui/material";
import { createListStore } from "../../common/listStore";
import { renderClose, useDialog, useSimpleDialog, yesNoButtons } from "../../common/dialog";
import { useState, useSyncExternalStore } from "react";
import { CustomerDto, RetentionSaveDto } from "./types";
import { combineLatest, distinctUntilChanged, map } from "rxjs";
import { createEntityStore, UseEntityStore } from "../../common/entityStore";
import {
    InvoiceStateId,
    OrderStateId,
    useInvoiceStates,
    useOrderStates
} from "../../common/enum";
import { propDateField, propNumberField, propTextField } from "../../common/entityStoreForm";
import shallow from "zustand/shallow";
import { customerOrderDialogTitle, getOrderState, useCustomerBasicInfo } from "./common";
import { listHeaderItems, renderListStore } from "../../common/listStoreUi";
import { orderBy } from "lodash";
import { OrderShipmentDueDateDialog } from "./OrderShipmentDueDateDialog";
import { OrderShipmentPaymentGatewayLinkDialog } from "./OrderShipmentPaymentGatewayLinkDialog";
import { AccountTree, Edit, Home, Payment } from "@mui/icons-material";
import { UserTypeId, useAccountStore } from "../../common/account";
import { createAxios } from "../../common/axios";
import { useDefaultValues } from "../../common/defaultValues";
import { landCodes } from "../../common/utils";
import { CustomerAddressDialog } from "./CustomerAddressDialog";

export function OrderShipmentsDialog(props: {
    orderId: number;
    ordId: number;
    close: () => void;
    useCustomerStore: UseEntityStore<CustomerDto>;
    retention: RetentionSaveDto | undefined;
    clearRetention(): void;
}) {
    const customer = useCustomerBasicInfo(props.useCustomerStore);
    const [{ useOrderStore, useShipmentsStore, useDetailsStore, useDunningShipmentsStore, subscribeToShipmentsStoreListPropsUpdate }] = useState(() => {
        const useOrderStore = createEntityStore<Partial<OrderDto>>({
            path: "customers/orders",
            id: props.orderId,
            data: {},
        });

        const useShipmentsStore = createListStore<OrderShipmentDto>({
            data: useOrderStore.getState().clientData$.pipe(
                map(p => ({ items: p.data.orderShipments || [], isFetching: p.isFetching })),
                distinctUntilChanged(shallow)),
            itemsPerPage: 3, autoSelectFirstItem: true,
        });

        function subscribeToShipmentsStoreListPropsUpdate() {
            const sub = useDefaultValues.getState().clientData$.subscribe({
                next(def) {
                    useShipmentsStore.getState().setListProps([
                        { column: "shpId", label: "Shp", width: 50, type: "number" },
                        { column: "deliveryAddressPosition", label: "Del.A", width: 50, type: "number" },
                        { column: "invcId", label: "Invc. number", width: 100 },
                        { column: "shpDte", label: "Deliv. date", width: 95, type: "date" },
                        { column: "invcDte", label: "Invc. date", width: 95, type: "date" },
                        { column: "dueDte", label: "Due date", width: 95, type: "date" },
                        { column: "discountAmountTotal", label: "Dsc. amt", width: 80, type: "currency" },
                        { column: "btoAmt", label: "Invc. amt", width: 80, type: "currency" },
                        { column: "rdcInvcAmt", label: "Rdc. amt", width: 80, type: "currency" },
                        { column: "invoiceStateId", label: "Invoice status", width: 135, enumStore: useInvoiceStates },
                        {
                            column: "pstNr", label: "Post number", width:
                                landCodes("EE", "LV").includes(def.data.landCode) ? 170 :
                                    landCodes("HU").includes(def.data.landCode) ? 225 :
                                        130
                        },
                        { column: "offerInvcId", label: "Offer invoice", width: 95 },
                        { column: "offerAmount", label: "Offer amt", width: 90, type: "currency" },
                        { column: "offerType", label: "Offer type", width: 350 },
                    ]);
                },
            });

            return () => sub.unsubscribe();
        }

        const useDetailsStore = createListStore<OrderShipmentDetailDto>({
            data: combineLatest([useShipmentsStore.getState().selectedItem$, useOrderStore.getState().clientData$]).pipe(
                map(([p, r]) => ({
                    items: p == null ? [] :
                        orderBy(p.orderShipmentDetails.concat(p.orderShipmentAutos), p => p.articleCode),
                    isFetching: r.isFetching
                })),
                distinctUntilChanged(shallow)),
            itemsPerPage: 3, listProps: [
                { column: "articleCode", label: "Art", width: 70 },
                { column: "articleName", label: "Description", width: 400 },
                { column: "packQty", label: "Qty", width: 70, align: "right" },
                { column: "btoAmt", label: "Price", width: 90, type: "currency" },
            ]
        });

        const useDunningShipmentsStore = createListStore<DunningShipmentDto>({
            path: "customers/dunningShipments?customerId=" + customer.id,
            itemsPerPage: 3, listProps: [
                { column: "letCde", label: "Dun", width: 120 },
                { column: "invcId", label: "Invc. number", width: 100 },
                { column: "shpDte", label: "Deliv. date", width: 100, type: "date" },
                { column: "invcDte", label: "Invc. date", width: 100, type: "date" },
                { column: "dueDte", label: "Due date", width: 100, type: "date" },
                { column: "btoAmt", label: "Dun. fee", width: 80, type: "currency" },
                { column: "dunningAmountTotal", label: "Dun. amt", width: 80, type: "currency" },
                { column: "invoiceStateId", label: "Invoice status", width: 150, enumStore: useInvoiceStates },
            ]
        });

        return { useOrderStore, useShipmentsStore, useDetailsStore, useDunningShipmentsStore, subscribeToShipmentsStoreListPropsUpdate };
    });

    useSyncExternalStore(subscribeToShipmentsStoreListPropsUpdate, () => null);

    const def = useDefaultValues().serverData;

    const orderStore = useOrderStore(p => ({
        serverData: p.serverData, isFetching: p.isFetching,
        product: p.clientData.product, project: p.clientData.project,
    }));

    const orderStates = useOrderStates();

    const selectedShipment = useShipmentsStore(p => p.selectedItem);
    const selectedDunning = useDunningShipmentsStore(p => p.selectedItem);

    const accountStore = useAccountStore(p => ({ userTypeId: p.data?.accessTokenDecoded.userTypeId }));
    const canManage = accountStore.userTypeId !== UserTypeId.View && accountStore.userTypeId !== UserTypeId.CollectionAgency;
    const canManageWriteOffs = accountStore.userTypeId === UserTypeId.Admin || accountStore.userTypeId === UserTypeId.Supervisor;

    const shipmentDueDateDialog = useDialog(close => <OrderShipmentDueDateDialog close={close}
        onUpdated={() => useOrderStore.getState().fetchServerData(true)}
        ordId={props.ordId} shipment={selectedShipment!} useCustomerStore={props.useCustomerStore}
        product={orderStore.product!} project={orderStore.project!}
        retention={props.retention && { ...props.retention, orderId: props.orderId }} clearRetention={props.clearRetention}
    />, "xs");

    const dunningDueDateDialog = useDialog(close => <OrderShipmentDueDateDialog close={close}
        onUpdated={() => useDunningShipmentsStore.getState().fetch()}
        ordId={selectedDunning!.ordId} shipment={selectedDunning!} useCustomerStore={props.useCustomerStore}
        product={selectedDunning!.product} project={selectedDunning!.project}
        retention={props.retention && { ...props.retention, orderId: props.orderId }} clearRetention={props.clearRetention}
    />, "xs");

    const shipmentPaymentGatewayLinkDialog = useDialog(close => <OrderShipmentPaymentGatewayLinkDialog close={close}
        onUpdated={() => useOrderStore.getState().fetchServerData(true)}
        shipment={selectedShipment!}
    />, "xs");

    const dunningPaymentGatewayLinkDialog = useDialog(close => <OrderShipmentPaymentGatewayLinkDialog close={close}
        onUpdated={() => useDunningShipmentsStore.getState().fetch()}
        shipment={selectedDunning!}
    />, "xs");

    const canChangeShipmentDueDate = selectedShipment != null;
    const canChangeDunningDueDate = selectedDunning != null;
    const canCreateShipmentPaymentLink = selectedShipment != null && selectedShipment.invoiceStateId === InvoiceStateId.Open;
    const canCreateDunningPaymentLink = selectedDunning != null && selectedDunning.invoiceStateId === InvoiceStateId.Open && selectedDunning === useDunningShipmentsStore.getState().items.at(0);
    const canWriteOffShipment = selectedShipment != null;

    const simpleDialog = useSimpleDialog();

    function changeShipmentDueDate() {
        if (selectedShipment!.invoiceStateId !== InvoiceStateId.Open) {
            simpleDialog.show("Error", "Due date change is allowed on open invoices only." +
                (selectedShipment!.invoiceStateId === InvoiceStateId.Dunned ? " Try changing due date of the last dunning invoice." : ""));
            return;
        }
        shipmentDueDateDialog.show();
    }

    function changeDunningDueDate() {
        if (selectedDunning !== useDunningShipmentsStore.getState().items.at(0)) {
            simpleDialog.show("Error", "Only last dunning can be changed.");
            return;
        }
        dunningDueDateDialog.show();
    }

    function writeOffShipment() {
        if (selectedShipment!.invoiceStateId !== InvoiceStateId.Open && selectedShipment!.invoiceStateId !== InvoiceStateId.Dunned) {
            simpleDialog.show("Error", "Only open and dunned invoices can be written off.");
            return;
        }

        simpleDialog.show("W/O confirmation", `Are you sure you want to write-off invoice ${selectedShipment!.invcId}?`, undefined, yesNoButtons(async () => {
            try {
                await createAxios().post(`customers/orderShipments/${selectedShipment!.id}/writeOffs`);
            } finally {
                useOrderStore.getState().fetchServerData(true);
                useDunningShipmentsStore.getState().fetch();
                props.useCustomerStore.getState().fetchServerData();
            }
        }));
    }

    const addresses = props.useCustomerStore(p => p.serverData.customerAddresses);
    const deliveryAddress = selectedShipment?.deliveryAddressId == null ? null :
        addresses.find(p => p.id === selectedShipment.deliveryAddressId);
    const deliveryAddressDialog = useDialog(close => <CustomerAddressDialog close={close}
        customerAddress={deliveryAddress} disabled useCustomerStore={props.useCustomerStore} />, "md");

    return <>
        {simpleDialog.render()}
        {shipmentDueDateDialog.render()}
        {shipmentPaymentGatewayLinkDialog.render()}
        {dunningDueDateDialog.render()}
        {deliveryAddressDialog.render()}
        {dunningPaymentGatewayLinkDialog.render()}
        <DialogTitle>{customerOrderDialogTitle(props.ordId, false, () => "Shipments", customer)}</DialogTitle>
        {renderClose(props.close)}
        <DialogContent>
            <Grid container columnSpacing={4}>
                <Grid container item xs={2} alignSelf="flex-start">
                    <Grid item xs={12}>{propTextField(useOrderStore)("product", "Product", { disabled: true })}</Grid>
                    <Grid item xs={12}>{propTextField(useOrderStore)("project", "Project", { disabled: true })}</Grid>
                    <Grid item xs={12}>{propTextField(useOrderStore)("sc", "Media code", { disabled: true })}</Grid>
                    <Grid item xs={12}><TextField value={orderStore.serverData.batNo || ""} label="Batch number" InputProps={{ disabled: true }} /></Grid>
                    <Grid item xs={12}>{propDateField(useOrderStore)("ordDte", null, "Enrolment date", { disabled: true })}</Grid>
                    <Grid item xs={12}>{propNumberField(useOrderStore)("shpUn", null, "Ordered quantity", { disabled: true })}</Grid>
                    <Grid item xs={12}><TextField value={getOrderState(orderStore.serverData, orderStates.items)} label="Status" InputProps={{ disabled: true }} /></Grid>
                </Grid>
                <Grid container item xs={10} alignSelf="flex-start" rowSpacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="h6">Shipments</Typography>
                        {renderListStore(useShipmentsStore, {
                            assumeHavingHorizontalScrollbar: true,
                            onDoubleClick() { canChangeShipmentDueDate && changeShipmentDueDate(); },
                            header: listHeaderItems([
                                canManage && ["dueDate", () => <Button title="Change due date" disabled={!canChangeShipmentDueDate}
                                    onClick={changeShipmentDueDate} color="edit"><Edit /></Button>],
                                canManageWriteOffs && ["wo", () => <Button title="Manual w/o" disabled={!canWriteOffShipment}
                                    onClick={writeOffShipment}><AccountTree /></Button>],
                                ["deliveryAddress", () => <Button title="View delivery address" disabled={deliveryAddress == null}
                                    onClick={() => deliveryAddressDialog.show()}><Home /></Button>],
                                def.allowComgatePaymentLinks && canManage && ["paymentGatewayLink", () => <Button title="Create payment link" disabled={!canCreateShipmentPaymentLink}
                                    onClick={() => shipmentPaymentGatewayLinkDialog.show()}><Payment /></Button>],
                            ])
                        })}
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h6">Articles</Typography>
                        {renderListStore(useDetailsStore)}
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h6">Dunnings</Typography>
                        {renderListStore(useDunningShipmentsStore, {
                            assumeHavingHorizontalScrollbar: true,
                            onDoubleClick() { canChangeDunningDueDate && changeDunningDueDate(); },
                            header: listHeaderItems([
                                canManage && ["dueDate", () => <Button title="Change due date" disabled={!canChangeDunningDueDate}
                                    onClick={changeDunningDueDate} color="edit"><Edit /></Button>],
                                def.allowComgatePaymentLinks && canManage && ["paymentGatewayLink", () => <Button title="Create payment link" disabled={!canCreateDunningPaymentLink}
                                    onClick={() => dunningPaymentGatewayLinkDialog.show()}><Payment /></Button>],
                            ])
                        })}
                    </Grid>
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <Button onClick={props.close} color="negative" variant="outlined">Close</Button>
        </DialogActions>
    </>;
}

export interface OrderDto {
    id: number;
    ordId: number;
    sc: string;
    shpUn: number;
    batNo: number;
    orderStateId: OrderStateId;
    act: boolean;
    ordDte: string;
    product: string;
    project: string;
    orderShipments: OrderShipmentDto[];
}

interface OrderShipmentDto {
    id: number;
    shpId: number;
    invcId: string;
    shpDte: string;
    invcDte: string;
    dueDte: string;
    discountAmountTotal: number;
    btoAmt: number;
    rdcInvcAmt: number;
    invoiceStateId: InvoiceStateId;
    pstNr: string;
    offerInvcId: string;
    offerAmount: number | null;
    offerType: string;
    orderShipmentDetails: OrderShipmentDetailDto[];
    orderShipmentAutos: OrderShipmentDetailDto[];
    deliveryAddressId: number | null;
    deliveryAddressPosition: number | null;
    paymentGatewayLink: string;
}

interface OrderShipmentDetailDto {
    id: number;
    packQty: number;
    articleCode: string;
    articleName: string;
    btoAmt: number;
}

interface DunningShipmentDto {
    id: number;
    shpId: number;
    letCde: string;
    invcId: string;
    shpDte: string;
    invcDte: string;
    dueDte: string;
    btoAmt: number;
    dunningAmountTotal: number;
    invoiceStateId: InvoiceStateId;
    ordId: number;
    product: string;
    project: string;
    paymentGatewayLink: string;
}
